<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-medical-bag me-2"></i>{{ $t('page.theatre', 'Theatre & Surgery') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button v-if="tab === 'surgeries'" class="hms-btn hms-btn-primary" @click="openBookSurgery">
            <i class="mdi mdi-calendar-plus me-1"></i>Book Surgery
          </button>
          <button v-if="tab === 'theatres'" class="hms-btn hms-btn-primary" @click="openAddTheatre">
            <i class="mdi mdi-plus me-1"></i>Add Theatre
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="setStatusFilter('scheduled')" :class="{ selected: tab==='surgeries' && statusFilter==='scheduled' }">
          <div class="hms-stat-num hms-stat-info">{{ countBy('scheduled') }}</div>
          <div class="hms-stat-lbl">Scheduled</div>
        </div>
        <div class="hms-stat" @click="setStatusFilter('in_progress')" :class="{ selected: tab==='surgeries' && statusFilter==='in_progress' }">
          <div class="hms-stat-num hms-stat-amber">{{ countBy('in_progress') }}</div>
          <div class="hms-stat-lbl">In Progress</div>
        </div>
        <div class="hms-stat" @click="setStatusFilter('completed')" :class="{ selected: tab==='surgeries' && statusFilter==='completed' }">
          <div class="hms-stat-num hms-stat-green">{{ countBy('completed') }}</div>
          <div class="hms-stat-lbl">Completed</div>
        </div>
        <div class="hms-stat" @click="setStatusFilter('cancelled')" :class="{ selected: tab==='surgeries' && statusFilter==='cancelled' }">
          <div class="hms-stat-num hms-stat-red">{{ countBy('cancelled') }}</div>
          <div class="hms-stat-lbl">Cancelled</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-purple">{{ todayCount }}</div>
          <div class="hms-stat-lbl">Today</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">{{ availableTheatres }}/{{ theatres.length }}</div>
          <div class="hms-stat-lbl">Theatres Available</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab === 'surgeries' }" @click="tab = 'surgeries'">
          <i class="mdi mdi-calendar-clock me-1"></i>Surgeries
          <span class="hms-tab-count">{{ surgeries.length }}</span>
        </button>
        <button class="hms-tab" :class="{ active: tab === 'board' }" @click="tab = 'board'">
          <i class="mdi mdi-view-dashboard-outline me-1"></i>Live Status Board
        </button>
        <button class="hms-tab" :class="{ active: tab === 'theatres' }" @click="tab = 'theatres'">
          <i class="mdi mdi-door me-1"></i>Theatres
        </button>
      </div>

      <!-- ============ LIVE STATUS BOARD ============ -->
      <div v-if="tab === 'board'">
        <div class="row g-3">
          <div class="col-md-4 col-lg" v-for="col in boardColumns" :key="col.key">
            <div class="hms-card h-100" :style="'border-top: 4px solid ' + col.color + ';'">
              <div class="hms-card-head d-flex justify-content-between">
                <span><i :class="col.icon" class="me-1"></i><strong>{{ col.label }}</strong></span>
                <span class="hms-badge" :style="'background:' + col.color + '; color:#fff;'">{{ surgeriesByStatus(col.key).length }}</span>
              </div>
              <div class="hms-card-body p-2">
                <div v-if="!surgeriesByStatus(col.key).length" class="hms-muted text-center py-3 hms-muted-sm">—</div>
                <div v-for="s in surgeriesByStatus(col.key)" :key="s.surgery_id" class="hms-surg-tile mb-2 p-2" style="background:#f7f8fb; border-radius:6px; cursor:pointer;" @click="openSurgery(s)">
                  <div class="fw-semibold">{{ s.patient_name || ('Patient #' + s.patient_id) }}</div>
                  <div class="hms-muted-sm">{{ s.procedure_name }}</div>
                  <div class="hms-muted-sm"><i class="mdi mdi-doctor me-1"></i>{{ s.surgeon_name || '—' }}<span v-if="s.theatre_name"> · <i class="mdi mdi-door me-1"></i>{{ s.theatre_name }}</span></div>
                  <div class="hms-muted-sm"><i class="mdi mdi-clock-outline me-1"></i>{{ formatDate(s.scheduled_date) }} {{ formatTime(s.scheduled_date) }} · {{ s.scheduled_duration }}min</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ============ SURGERIES TAB ============ -->
      <div v-else-if="tab === 'surgeries'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="search" placeholder="Search patient, procedure, surgery #..." />
            </div>
            <select class="hms-input hms-filter" v-model="statusFilter">
              <option value="">All</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="postponed">Postponed</option>
            </select>
            <select class="hms-input hms-filter" v-model="typeFilter">
              <option value="">All types</option>
              <option value="elective">Elective</option>
              <option value="urgent">Urgent</option>
              <option value="emergency">Emergency</option>
            </select>
            <span class="hms-muted ms-auto">{{ filteredSurgeries.length }} surgeries</span>
          </div>

          <div v-if="filteredSurgeries.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Surgery</th>
                <th>Patient</th>
                <th>Procedure</th>
                <th>Theatre</th>
                <th>Surgeon</th>
                <th>Scheduled</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                <tr v-for="s in filteredSurgeries" :key="s.surgery_id">
                  <td><span class="hms-mono">{{ s.surgery_number }}</span></td>
                  <td>
                    <div class="hms-text fw-semibold">{{ s.patient_name }}</div>
                    <div class="hms-muted-sm">{{ s.patient_no }}</div>
                  </td>
                  <td>
                    <div>{{ s.procedure_name }}</div>
                    <div class="hms-muted-sm" v-if="s.icd_procedure_code">ICD: {{ s.icd_procedure_code }}</div>
                  </td>
                  <td>{{ s.theatre_name || '—' }}</td>
                  <td>{{ s.surgeon_name || '—' }}</td>
                  <td>
                    <div>{{ formatDate(s.scheduled_date) }}</div>
                    <div class="hms-muted-sm">{{ formatTime(s.scheduled_date) }} · {{ s.scheduled_duration }} min</div>
                  </td>
                  <td><span class="hms-badge" :class="surgTypeBadge(s.surgery_type)">{{ s.surgery_type }}</span></td>
                  <td><span class="hms-badge" :class="surgStatusBadge(s.status)">{{ formatSurgStatus(s.status) }}</span></td>
                  <td>
                    <button class="hms-btn hms-btn-sm hms-btn-blue" @click="openSurgery(s)" title="Open">
                      <i class="mdi mdi-arrow-right me-1"></i>Open
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-calendar-blank-outline"></i>No surgeries match.</div>
        </div>
      </div>

      <!-- ============ THEATRES TAB ============ -->
      <div v-else-if="tab === 'theatres'">
        <div class="hms-card">
          <div v-if="theatres.length" class="hms-theatre-grid">
            <div v-for="t in theatres" :key="t.theatre_id" class="hms-theatre-card" :class="theatreCardClass(t.status)">
              <div class="hms-theatre-icon"><i class="mdi mdi-door"></i></div>
              <div class="hms-theatre-body">
                <div class="hms-theatre-name">{{ t.theatre_name }}</div>
                <div class="hms-muted-sm">{{ t.theatre_code || '' }} · {{ formatTheatreType(t.theatre_type) }}</div>
                <div class="mt-2">
                  <span class="hms-badge" :class="theatreBadge(t.status)">{{ formatTheatreStatus(t.status) }}</span>
                </div>
                <div class="mt-3 d-flex flex-wrap gap-1">
                  <button v-for="opt in theatreStatuses" :key="opt"
                          class="hms-btn hms-btn-sm"
                          :class="{ 'hms-btn-primary': opt === t.status, 'hms-btn-blue': opt !== t.status }"
                          @click="changeTheatreStatus(t, opt)">
                    {{ formatTheatreStatus(opt) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="hms-empty">
            <i class="mdi mdi-door"></i>No theatres yet — click <strong>Add Theatre</strong>.
          </div>
        </div>
      </div>

      <!-- ============ BOOK SURGERY MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showBook" @click.self="showBook=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-calendar-plus me-2"></i>Book Surgery</span>
            <button class="hms-modal-close" @click="showBook=false">&times;</button>
          </div>
          <div v-if="bookError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ bookError }}</div>

          <!-- Patient picker -->
          <div class="mb-3">
            <label class="hms-label">Patient *</label>
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="patientSearch" placeholder="Search name, patient no, phone..." @input="onPatientSearch" />
            </div>
            <div v-if="patientResults.length" class="hms-suggest">
              <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
                <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
                <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.phone || '—' }}</div>
              </div>
            </div>
            <div v-if="bookForm.patient_id" class="hms-picked mt-2">
              <i class="mdi mdi-check-circle me-1"></i>{{ bookForm.patient_name }} ({{ bookForm.patient_no }})
              <button class="hms-modal-close ms-auto" @click="clearPatient">&times;</button>
            </div>
          </div>

          <div class="hms-section-label">PROCEDURE</div>
          <div class="row g-3 mb-3">
            <div class="col-md-8"><label class="hms-label">Procedure Name *</label>
              <input class="hms-input" v-model="bookForm.procedure_name" placeholder="e.g. Elective Caesarean Section" />
            </div>
            <div class="col-md-4"><label class="hms-label">ICD Procedure Code</label>
              <input class="hms-input" v-model="bookForm.icd_procedure_code" placeholder="74.99" />
            </div>
            <div class="col-md-4"><label class="hms-label">Surgery Type</label>
              <select class="hms-input" v-model="bookForm.surgery_type">
                <option value="elective">Elective</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Anaesthesia</label>
              <select class="hms-input" v-model="bookForm.anaesthesia_type">
                <option value="">Select...</option>
                <option value="General">General</option>
                <option value="Spinal">Spinal</option>
                <option value="Epidural">Epidural</option>
                <option value="Local">Local</option>
                <option value="Regional">Regional</option>
                <option value="Sedation">Sedation</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Theatre</label>
              <select class="hms-input" v-model="bookForm.theatre_id">
                <option value="">Select...</option>
                <option v-for="t in theatres" :key="t.theatre_id" :value="t.theatre_id">{{ t.theatre_name }}</option>
              </select>
            </div>
          </div>

          <div class="hms-section-label">TEAM</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Surgeon</label>
              <select class="hms-input" v-model="bookForm.surgeon_id">
                <option value="">Select...</option>
                <option v-for="d in doctors" :key="d.user_id" :value="d.user_id">{{ d.first_name }} {{ d.last_name }}</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Anaesthetist</label>
              <select class="hms-input" v-model="bookForm.anaesthetist_id">
                <option value="">Select...</option>
                <option v-for="d in doctors" :key="d.user_id" :value="d.user_id">{{ d.first_name }} {{ d.last_name }}</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Scrub Nurse</label>
              <select class="hms-input" v-model="bookForm.scrub_nurse_id">
                <option value="">Select...</option>
                <option v-for="n in nurses" :key="n.user_id" :value="n.user_id">{{ n.first_name }} {{ n.last_name }}</option>
              </select>
            </div>
          </div>

          <div class="hms-section-label">SCHEDULE</div>
          <div class="row g-3 mb-3">
            <div class="col-md-8"><label class="hms-label">Scheduled Date &amp; Time *</label>
              <input type="datetime-local" class="hms-input" v-model="bookForm.scheduled_date" />
            </div>
            <div class="col-md-4"><label class="hms-label">Duration (minutes)</label>
              <input type="number" class="hms-input" v-model.number="bookForm.scheduled_duration" min="15" step="15" />
            </div>
            <div class="col-12"><label class="hms-label">Pre-Op Notes</label>
              <textarea class="hms-input" rows="2" v-model="bookForm.pre_op_notes" placeholder="Pre-operative considerations, special equipment, etc."></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doBook" :disabled="processing || !bookForm.patient_id || !bookForm.procedure_name || !bookForm.scheduled_date">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Booking...' : 'Book Surgery' }}
          </button>
        </div>
      </div>

      <!-- ============ SURGERY DETAIL MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showDetail" @click.self="closeDetail">
        <div class="hms-modal" v-if="detailLoading || surgeryDetail">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-medical-bag me-2"></i>{{ surgeryDetail ? surgeryDetail.surgery_number : 'Surgery' }}</span>
            <button class="hms-modal-close" @click="closeDetail">&times;</button>
          </div>

          <div v-if="detailLoading" class="hms-spinner py-3"><div class="spinner-border"></div></div>

          <div v-else-if="surgeryDetail">
            <div class="hms-kv mb-3">
              <div><span class="hms-kv-key">Patient</span><span>{{ surgeryDetail.patient_name }} ({{ surgeryDetail.patient_no }})</span></div>
              <div><span class="hms-kv-key">Procedure</span><span>{{ surgeryDetail.procedure_name }}</span></div>
              <div v-if="surgeryDetail.icd_procedure_code"><span class="hms-kv-key">ICD Code</span><span class="hms-mono">{{ surgeryDetail.icd_procedure_code }}</span></div>
              <div><span class="hms-kv-key">Type</span><span><span class="hms-badge" :class="surgTypeBadge(surgeryDetail.surgery_type)">{{ surgeryDetail.surgery_type }}</span></span></div>
              <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="surgStatusBadge(surgeryDetail.status)">{{ formatSurgStatus(surgeryDetail.status) }}</span></span></div>
              <div><span class="hms-kv-key">Theatre</span><span>{{ surgeryDetail.theatre_name || '—' }}</span></div>
              <div><span class="hms-kv-key">Scheduled</span><span>{{ formatDateTime(surgeryDetail.scheduled_date) }} · {{ surgeryDetail.scheduled_duration }} min</span></div>
              <div v-if="surgeryDetail.start_time"><span class="hms-kv-key">Start</span><span>{{ formatDateTime(surgeryDetail.start_time) }}</span></div>
              <div v-if="surgeryDetail.end_time"><span class="hms-kv-key">End</span><span>{{ formatDateTime(surgeryDetail.end_time) }}</span></div>
              <div v-if="surgeryDetail.anaesthesia_type"><span class="hms-kv-key">Anaesthesia</span><span>{{ surgeryDetail.anaesthesia_type }}</span></div>
            </div>

            <div class="hms-section-label">SURGICAL TEAM</div>
            <div class="hms-kv mb-3">
              <div><span class="hms-kv-key">Surgeon</span><span>{{ surgeryDetail.surgeon_name || '—' }}</span></div>
              <div><span class="hms-kv-key">Anaesthetist</span><span>{{ surgeryDetail.anaesthetist_name || '—' }}</span></div>
              <div><span class="hms-kv-key">Scrub Nurse</span><span>{{ surgeryDetail.scrub_nurse_name || '—' }}</span></div>
            </div>

            <div v-if="surgeryDetail.pre_op_notes">
              <div class="hms-section-label">PRE-OP NOTES</div>
              <div class="hms-note">{{ surgeryDetail.pre_op_notes }}</div>
            </div>

            <div v-if="surgeryDetail.intra_op_notes">
              <div class="hms-section-label">INTRA-OP NOTES</div>
              <div class="hms-note">{{ surgeryDetail.intra_op_notes }}</div>
            </div>

            <div v-if="surgeryDetail.post_op_notes">
              <div class="hms-section-label">POST-OP NOTES</div>
              <div class="hms-note">{{ surgeryDetail.post_op_notes }}</div>
            </div>

            <div v-if="surgeryDetail.complications">
              <div class="hms-section-label">COMPLICATIONS</div>
              <div class="hms-note hms-note-warn">{{ surgeryDetail.complications }}</div>
            </div>

            <div v-if="surgeryDetail.outcome">
              <div class="hms-section-label">OUTCOME</div>
              <div class="hms-note"><span class="hms-badge" :class="outcomeBadge(surgeryDetail.outcome)">{{ formatOutcome(surgeryDetail.outcome) }}</span></div>
            </div>

            <!-- State-driven actions -->
            <div class="d-flex gap-2 mt-3 flex-wrap">
              <!-- Scheduled -->
              <template v-if="surgeryDetail.status === 'scheduled'">
                <button class="hms-btn hms-btn-primary" @click="startSurgery" :disabled="processing">
                  <i class="mdi mdi-play me-1"></i>Start Surgery
                </button>
                <button class="hms-btn hms-btn-amber" @click="openPostpone">
                  <i class="mdi mdi-clock-outline me-1"></i>Postpone
                </button>
                <button class="hms-btn hms-btn-red" @click="openCancel">
                  <i class="mdi mdi-close me-1"></i>Cancel
                </button>
              </template>

              <!-- In progress → Complete form -->
              <button v-if="surgeryDetail.status === 'in_progress'" class="hms-btn hms-btn-success" @click="openComplete">
                <i class="mdi mdi-check-circle me-1"></i>Complete Surgery
              </button>

              <!-- Postponed → reschedule -->
              <button v-if="surgeryDetail.status === 'postponed'" class="hms-btn hms-btn-primary" @click="openPostpone">
                <i class="mdi mdi-calendar-edit me-1"></i>Reschedule
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ COMPLETE SURGERY MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showComplete" @click.self="showComplete=false">
        <div class="hms-modal" style="max-width:640px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-check-circle me-2"></i>Complete Surgery</span>
            <button class="hms-modal-close" @click="showComplete=false">&times;</button>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Outcome *</label>
              <select class="hms-input" v-model="completeForm.outcome">
                <option value="successful">Successful</option>
                <option value="complicated">Complicated</option>
                <option value="abandoned">Abandoned</option>
                <option value="patient_deceased">Patient Deceased</option>
              </select>
            </div>
          </div>
          <div class="mb-3"><label class="hms-label">Intra-Op Notes</label>
            <textarea class="hms-input" rows="3" v-model="completeForm.intra_op_notes" placeholder="What was done during the surgery"></textarea>
          </div>
          <div class="mb-3"><label class="hms-label">Post-Op Notes</label>
            <textarea class="hms-input" rows="3" v-model="completeForm.post_op_notes" placeholder="Recovery instructions, post-op orders"></textarea>
          </div>
          <div class="mb-3"><label class="hms-label">Complications (if any)</label>
            <textarea class="hms-input" rows="2" v-model="completeForm.complications"></textarea>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doComplete" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : 'Mark Complete' }}
          </button>
        </div>
      </div>

      <!-- ============ POSTPONE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showPostpone" @click.self="showPostpone=false">
        <div class="hms-modal" style="max-width:480px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-clock-outline me-2"></i>Reschedule Surgery</span>
            <button class="hms-modal-close" @click="showPostpone=false">&times;</button>
          </div>
          <div class="mb-3"><label class="hms-label">New Date &amp; Time *</label>
            <input type="datetime-local" class="hms-input" v-model="postponeDate" />
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="doPostpone" :disabled="processing || !postponeDate">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Save New Date
          </button>
        </div>
      </div>

      <!-- ============ CANCEL MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showCancel" @click.self="showCancel=false">
        <div class="hms-modal" style="max-width:480px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-close-circle me-2 text-danger"></i>Cancel Surgery</span>
            <button class="hms-modal-close" @click="showCancel=false">&times;</button>
          </div>
          <div class="mb-3"><label class="hms-label">Reason</label>
            <textarea class="hms-input" rows="3" v-model="cancelReason" placeholder="Why is this surgery being cancelled?"></textarea>
          </div>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-blue flex-fill" @click="showCancel=false">Don't cancel</button>
            <button class="hms-btn hms-btn-red flex-fill" @click="doCancel" :disabled="processing">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Confirm Cancel
            </button>
          </div>
        </div>
      </div>

      <!-- ============ ADD THEATRE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showAddTheatre" @click.self="showAddTheatre=false">
        <div class="hms-modal" style="max-width:480px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-plus-circle me-2"></i>Add Theatre</span>
            <button class="hms-modal-close" @click="showAddTheatre=false">&times;</button>
          </div>
          <div v-if="theatreError" class="hms-alert hms-alert-danger">{{ theatreError }}</div>
          <div class="row g-3 mb-3">
            <div class="col-8"><label class="hms-label">Theatre Name *</label>
              <input class="hms-input" v-model="theatreForm.theatre_name" placeholder="Main Theatre" />
            </div>
            <div class="col-4"><label class="hms-label">Code</label>
              <input class="hms-input" v-model="theatreForm.theatre_code" placeholder="MOT" />
            </div>
            <div class="col-12"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="theatreForm.theatre_type">
                <option value="main">Main</option>
                <option value="emergency">Emergency</option>
                <option value="minor">Minor</option>
                <option value="obstetric">Obstetric</option>
                <option value="endoscopy">Endoscopy</option>
              </select>
            </div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="saveTheatre" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Add Theatre
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: var(--hms-h); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-amber { color: #f1b44c; }
.hms-stat-info { color: var(--hms-primary); }
.hms-stat-purple { color: #9b59b6; }
.hms-stat-green { color: var(--hms-success); }
.hms-stat-red { color: var(--hms-danger); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tab-count { background: var(--hms-primary); color: #fff; border-radius: 999px; padding: 1px 7px; font-size: 10px; margin-left: 8px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-top: 8px; margin-bottom: 8px; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-red    { background: rgba(220,53,69,.15);   color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-red:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-success { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-success:hover:not(:disabled) { background: var(--hms-success); color: #fff; }

/* Theatre cards */
.hms-theatre-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
.hms-theatre-card { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; display: flex; gap: 16px; align-items: flex-start; transition: all .15s; }
.hms-theatre-card.hms-theatre-available { border-color: rgba(40,167,69,.4); background: rgba(40,167,69,.04); }
.hms-theatre-card.hms-theatre-in_use    { border-color: rgba(245,158,11,.4); background: rgba(245,158,11,.04); }
.hms-theatre-card.hms-theatre-cleaning  { border-color: rgba(26,127,232,.4); background: var(--hms-primary-lt); }
.hms-theatre-card.hms-theatre-maintenance { border-color: var(--hms-br); background: var(--hms-bg3); opacity: 0.75; }
.hms-theatre-icon { width: 48px; height: 48px; border-radius: 12px; background: var(--hms-bg1); display: flex; align-items: center; justify-content: center; font-size: 28px; color: var(--hms-primary); flex-shrink: 0; }
.hms-theatre-body { flex: 1; min-width: 0; }
.hms-theatre-name { color: var(--hms-h); font-weight: 700; font-size: 16px; }

/* Modal */
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 140px; font-weight: 600; }

.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }

.hms-note { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 10px 14px; font-size: 13px; line-height: 1.6; color: var(--hms-h); white-space: pre-wrap; }
.hms-note-warn { background: rgba(255,193,7,.08); border-color: rgba(255,193,7,.3); }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "TheatreManagement",
  components: { Navigation },
  data() {
    return {
      loading: true,
      processing: false,
      tab: "surgeries",
      search: "",
      statusFilter: "",
      typeFilter: "",

      surgeries: [],
      theatres: [],
      doctors: [],
      nurses: [],
      theatreStatuses: ['available','in_use','cleaning','maintenance'],

      // Book surgery
      showBook: false,
      bookError: "",
      bookForm: this.emptyBookForm(),
      patientSearch: "",
      patientSearchTimer: null,
      patientResults: [],

      // Detail
      showDetail: false,
      detailLoading: false,
      surgeryDetail: null,

      // Complete
      showComplete: false,
      completeForm: { outcome: "successful", intra_op_notes: "", post_op_notes: "", complications: "" },

      // Postpone
      showPostpone: false,
      postponeDate: "",

      // Cancel
      showCancel: false,
      cancelReason: "",

      // Theatre form
      showAddTheatre: false,
      theatreError: "",
      theatreForm: { theatre_name: "", theatre_code: "", theatre_type: "main" },

      // Toast
      toastMsg: "",
      toastError: false,
      toastTimer: null,

      branchId:   sessionStorage.getItem("branchId"),
      hospitalId: sessionStorage.getItem("hospitalId")
    };
  },
  computed: {
    todayCount() {
      var today = new Date().toISOString().slice(0,10);
      return this.surgeries.filter(s => (s.scheduled_date || "").slice(0,10) === today).length;
    },
    availableTheatres() {
      return this.theatres.filter(t => t.status === 'available').length;
    },
    boardColumns() {
      return [
        { key: 'scheduled',   label: 'Queued',     icon: 'mdi mdi-calendar-clock',     color: '#0EA5E9' },
        { key: 'preop',       label: 'In-Prep',    icon: 'mdi mdi-clipboard-text',      color: '#F59E0B' },
        { key: 'in_progress', label: 'In Surgery', icon: 'mdi mdi-medical-bag',         color: '#DC2626' },
        { key: 'recovery',    label: 'Recovery',   icon: 'mdi mdi-bed-empty',           color: '#7C3AED' },
        { key: 'completed',   label: 'Discharged', icon: 'mdi mdi-check-circle-outline',color: '#10B981' }
      ];
    },
    filteredSurgeries() {
      var list = this.surgeries;
      if (this.statusFilter) list = list.filter(s => s.status === this.statusFilter);
      if (this.typeFilter)   list = list.filter(s => s.surgery_type === this.typeFilter);
      var q = this.search.trim().toLowerCase();
      if (q) {
        list = list.filter(s => ((s.patient_name || "") + " " + (s.surgery_number || "") + " " + (s.procedure_name || "") + " " + (s.patient_no || "")).toLowerCase().indexOf(q) >= 0);
      }
      return list;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() {
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    surgeriesByStatus(key) {
      return this.surgeries.filter(s => s.status === key);
    },
    emptyBookForm() {
      return {
        patient_id: null, patient_name: "", patient_no: "",
        admission_id: "",
        theatre_id: "", procedure_name: "", icd_procedure_code: "",
        surgery_type: "elective", anaesthesia_type: "",
        surgeon_id: "", anaesthetist_id: "", scrub_nurse_id: "",
        scheduled_date: "", scheduled_duration: 60, pre_op_notes: ""
      };
    },

    countBy(s) { return this.surgeries.filter(x => x.status === s).length; },
    setStatusFilter(s) { this.tab = "surgeries"; this.statusFilter = s; },

    formatDate(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleDateString(undefined, { day:'2-digit', month:'short', year:'numeric' }); } catch(e) { return d; }
    },
    formatTime(d) {
      if (!d) return "";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return ""; return dt.toLocaleTimeString(undefined, { hour:'2-digit', minute:'2-digit' }); } catch(e) { return ""; }
    },
    formatDateTime(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleString(undefined, { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); } catch(e) { return d; }
    },
    formatSurgStatus(s) {
      return ({ scheduled:'Scheduled', in_progress:'In Progress', completed:'Completed', cancelled:'Cancelled', postponed:'Postponed' })[s] || s;
    },
    surgStatusBadge(s) {
      var m = { scheduled:'hms-badge-info', in_progress:'hms-badge-amber', completed:'hms-badge-success', cancelled:'hms-badge-danger', postponed:'hms-badge-purple' };
      return m[s] || 'hms-badge-neutral';
    },
    surgTypeBadge(t) {
      return ({ emergency: 'hms-badge-danger', urgent: 'hms-badge-amber', elective: 'hms-badge-info' })[t] || 'hms-badge-neutral';
    },
    formatTheatreType(t) { return ({ main:'Main', emergency:'Emergency', minor:'Minor', obstetric:'Obstetric', endoscopy:'Endoscopy' })[t] || t; },
    formatTheatreStatus(s) { return ({ available:'Available', in_use:'In Use', cleaning:'Cleaning', maintenance:'Maintenance' })[s] || s; },
    theatreBadge(s) {
      var m = { available:'hms-badge-success', in_use:'hms-badge-amber', cleaning:'hms-badge-info', maintenance:'hms-badge-neutral' };
      return m[s] || 'hms-badge-neutral';
    },
    theatreCardClass(s) { return 'hms-theatre-' + s; },
    formatOutcome(o) { return ({ successful:'Successful', complicated:'Complicated', abandoned:'Abandoned', patient_deceased:'Patient Deceased' })[o] || o; },
    outcomeBadge(o) {
      switch(o) {
        case 'successful':         return 'hms-badge-success';
        case 'complicated':        return 'hms-badge-amber';
        case 'abandoned':          return 'hms-badge-danger';
        case 'patient_deceased':   return 'hms-badge-danger';
        default:                   return 'hms-badge-neutral';
      }
    },

    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    async loadAll() {
      this.loading = true;
      try { await Promise.all([this.loadSurgeries(), this.loadTheatres(), this.loadDoctors(), this.loadNurses()]); }
      catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadSurgeries() {
      try {
        var r = await executeJson({ function: "getSurgeries", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.surgeries = (r.data.data && r.data.data.Surgeries) || [];
      } catch(e) { console.error(e); }
    },
    async loadTheatres() {
      try {
        var r = await executeJson({ function: "getTheatres", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.theatres = (r.data.data && r.data.data.Theatres) || [];
      } catch(e) { console.error(e); }
    },
    async loadDoctors() {
      try {
        var r = await executeJson({ function: "getDoctors", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.doctors = (r.data.data && r.data.data.Doctors) || [];
      } catch(e) { console.error(e); }
    },
    async loadNurses() {
      try {
        var r = await executeJson({ function: "getStaff", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) {
          var all = (r.data.data && r.data.data.Staff) || [];
          this.nurses = all.filter(s => (s.role_id == 5) || /nurse/i.test(s.role_name || "") || /nurse/i.test(s.specialization || ""));
        }
      } catch(e) { /* getStaff may not exist; non-fatal */ }
    },

    // ===== BOOK =====
    openBookSurgery() {
      this.bookForm = this.emptyBookForm();
      this.bookError = "";
      this.patientSearch = "";
      this.patientResults = [];
      this.showBook = true;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
      var self = this;
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      this.patientSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) self.patientResults = (r.data.data && r.data.data.Patients) || [];
        } catch(e) {}
      }, 300);
    },
    pickPatient(p) {
      this.bookForm.patient_id = p.patient_id;
      this.bookForm.patient_name = (p.first_name + ' ' + p.last_name).trim();
      this.bookForm.patient_no = p.patient_no;
      this.patientResults = []; this.patientSearch = "";
    },
    clearPatient() { this.bookForm.patient_id = null; this.bookForm.patient_name = ""; this.bookForm.patient_no = ""; },
    async doBook() {
      this.bookError = "";
      if (!this.bookForm.patient_id) { this.bookError = "Pick a patient."; return; }
      if (!this.bookForm.procedure_name) { this.bookError = "Procedure name is required."; return; }
      if (!this.bookForm.scheduled_date) { this.bookError = "Scheduled date is required."; return; }

      var payload = Object.assign({ function: "bookSurgery" }, this.bookForm);
      // datetime-local gives "YYYY-MM-DDTHH:MM" — convert to MySQL datetime
      payload.scheduled_date = payload.scheduled_date.replace('T', ' ') + ':00';
      delete payload.patient_name;
      delete payload.patient_no;

      this.processing = true;
      try {
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast("Surgery booked: " + r.data.data.surgery_number);
          this.showBook = false;
          await this.loadSurgeries();
        } else { this.bookError = r.data.message || "Failed."; }
      } catch(e) { this.bookError = "Connection error."; }
      this.processing = false;
    },

    // ===== DETAIL =====
    async openSurgery(s) {
      this.showDetail = true;
      this.detailLoading = true;
      this.surgeryDetail = null;
      try {
        var r = await executeJson({ function: "getSurgeryDetail", surgery_id: s.surgery_id }, hms);
        if (r.data && r.data.success) this.surgeryDetail = r.data.data && r.data.data.Surgery;
      } catch(e) { this.showToast("Failed to load surgery", true); }
      this.detailLoading = false;
    },
    closeDetail() { this.showDetail = false; this.surgeryDetail = null; },

    async refreshDetail() {
      if (!this.surgeryDetail) return;
      var id = this.surgeryDetail.surgery_id;
      var r = await executeJson({ function: "getSurgeryDetail", surgery_id: id }, hms);
      if (r.data && r.data.success) this.surgeryDetail = r.data.data && r.data.data.Surgery;
    },

    async startSurgery() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "startSurgery", surgery_id: this.surgeryDetail.surgery_id }, hms);
        if (r.data && r.data.success) {
          this.showToast("Surgery started");
          await Promise.all([this.refreshDetail(), this.loadSurgeries(), this.loadTheatres()]);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openComplete() {
      this.completeForm = { outcome: "successful", intra_op_notes: "", post_op_notes: "", complications: "" };
      this.showComplete = true;
    },
    async doComplete() {
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "completeSurgery", surgery_id: this.surgeryDetail.surgery_id }, this.completeForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Surgery completed");
          this.showComplete = false;
          await Promise.all([this.refreshDetail(), this.loadSurgeries(), this.loadTheatres()]);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openPostpone() {
      this.postponeDate = "";
      this.showPostpone = true;
    },
    async doPostpone() {
      if (!this.postponeDate) return;
      var newDate = this.postponeDate.replace('T', ' ') + ':00';
      this.processing = true;
      try {
        var r = await executeJson({ function: "postponeSurgery", surgery_id: this.surgeryDetail.surgery_id, scheduled_date: newDate }, hms);
        if (r.data && r.data.success) {
          this.showToast("Surgery rescheduled");
          this.showPostpone = false;
          await Promise.all([this.refreshDetail(), this.loadSurgeries()]);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openCancel() { this.cancelReason = ""; this.showCancel = true; },
    async doCancel() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "cancelSurgery", surgery_id: this.surgeryDetail.surgery_id, reason: this.cancelReason }, hms);
        if (r.data && r.data.success) {
          this.showToast("Surgery cancelled");
          this.showCancel = false;
          await Promise.all([this.refreshDetail(), this.loadSurgeries()]);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    // ===== THEATRES =====
    openAddTheatre() {
      this.theatreForm = { theatre_name: "", theatre_code: "", theatre_type: "main" };
      this.theatreError = "";
      this.showAddTheatre = true;
    },
    async saveTheatre() {
      this.theatreError = "";
      if (!this.theatreForm.theatre_name) { this.theatreError = "Theatre name is required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createTheatre" }, this.theatreForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Theatre added");
          this.showAddTheatre = false;
          await this.loadTheatres();
        } else { this.theatreError = r.data.message || "Failed."; }
      } catch(e) { this.theatreError = "Connection error."; }
      this.processing = false;
    },
    async changeTheatreStatus(t, status) {
      if (t.status === status) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "updateTheatreStatus", theatre_id: t.theatre_id, status: status }, hms);
        if (r.data && r.data.success) {
          this.showToast("Theatre " + status);
          await this.loadTheatres();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    }
  }
};
</script>
